import {
    rpTranslatingMessage,
    rpTranslatedFromMessage,
    rpTranslationErrorMessage,
    rpTranslateMessage,
    rpNeverTranslateFromMessage,
    rpUntranslateMessage,
    rpTranslationPreferences,
    rpTurnOnAutomaticTranslation,
    rpTurnOnAutomaticInfoToolTip,
    rpTranslateWrongLanguageLinkMessage,
    rpTranslateWrongLanguageMessage,
    rpTurnOnAutomaticTranslationNew,
    rpDeclinedAutoTranslationMessage,
    rpYes,
    rpNo,
} from './createTranslateInfoBarMessage.locstring.json';
import loc, { format } from 'owa-localize';
import { ClientItem } from 'owa-mail-store';
import getTranslationOptOut from 'owa-translation/lib/utils/getTranslationOptOut';
import getConversationSubject from 'owa-mail-reading-pane-store/lib/utils/getConversationSubject';
import ItemViewState from 'owa-mail-reading-pane-store/lib/store/schema/ItemViewState';
import Message from 'owa-service/lib/contract/Message';
import shouldTranslateAutomatic from 'owa-translation/lib/utils/shouldTranslateAutomatic';
import shouldSuggestAutomatic from 'owa-translation/lib/utils/shouldSuggestAutomatic';
import TranslationMode from 'owa-translation/lib/store/schema/TranslationMode';
import { lazyMountAndShowFullOptions } from 'owa-options-view';
import {
    lazyTranslateItem,
    lazyUntranslateItem,
    lazySetWrongLanguage,
} from 'owa-mail-inline-translation';
import { lazyAddExcludedLanguage, lazyUpdateTranslationMode } from 'owa-translation';
import {
    InfoBarCustomAction,
    InfoBarInfoIcon,
    InfoBarMessageColor,
    InfoBarMessageRank,
    InfoBarMessageSource,
    InfoBarMessageViewState,
    MessagePart,
} from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import { logUsage } from 'owa-analytics';
import { isFeatureEnabled } from 'owa-feature-flags';
import { CustomData } from 'owa-analytics-actions';

// Message is shown if this string is equal to current message id,
// so it is cleared if user switches to different message
let showDeclinedAutoTranslationMessage: string = null;

export default function tryCreateTranslateInfoBarMessage(
    message: Message,
    key: string,
    itemViewState: ItemViewState
): InfoBarMessageViewState {
    const clientItem = message as ClientItem;

    if (
        isFeatureEnabled('rp-inlineTranslation') &&
        (itemViewState.isConversationItemPart ||
            (isFeatureEnabled('rp-inlineTranslation-conversationViewOff') &&
                !itemViewState.isConversationItemPart)) &&
        clientItem.TranslationData
    ) {
        const isBodyTranslatable =
            !getTranslationOptOut(clientItem.TranslationData.itemOgmaLocale) &&
            clientItem.TranslationData.isTranslatable;

        let isForwardContentTranslatable;
        if (isFeatureEnabled('rp-inlineTranslationForwardContent2')) {
            isForwardContentTranslatable = clientItem.TranslationData.forwardContentLocale
                ? !getTranslationOptOut(clientItem.TranslationData.forwardContentLocale) &&
                  clientItem.TranslationData.isForwardContentTranslatable
                : false;
        } else {
            isForwardContentTranslatable = false;
        }

        let subject: string;
        if (itemViewState.isConversationItemPart) {
            subject = getConversationSubject(clientItem.ConversationId?.Id);
        } else {
            subject = clientItem.Subject;
        }

        const itemId = clientItem.ItemId.Id;

        let infoBarMessageParts: MessagePart[] = [];

        if (clientItem.TranslationData.isTranslating) {
            infoBarMessageParts.push(loc(rpTranslatingMessage));
        } else if (
            clientItem.TranslationData.isShowingTranslation ||
            clientItem.TranslationData.isShowingForwardContentTranslation
        ) {
            const showTranslatedFrom = !clientItem.TranslationData.manuallyTranslated;
            const showTranslationPrefreneces =
                isFeatureEnabled('rp-inlineTranslationAutomatic') &&
                !clientItem.TranslationData.manuallyTranslated;

            infoBarMessageParts = createTranslatedParts(
                showTranslatedFrom,
                showTranslationPrefreneces,
                clientItem,
                subject,
                itemViewState
            );
        } else if (clientItem.TranslationData.isWrongLanguage) {
            infoBarMessageParts.push(loc(rpTranslateWrongLanguageMessage));
        } else if (
            (clientItem.TranslationData.isTranslatable && isBodyTranslatable) ||
            (clientItem.TranslationData.isForwardContentTranslatable &&
                isForwardContentTranslatable)
        ) {
            // don't log additional traces after untranslate
            if (!clientItem.TranslationData.translationText) {
                const customData: CustomData = clientItem.TranslationData.isFastTextLanguage
                    ? { fastText: true }
                    : [];
                logUsage('InlineTranslationSuggestionShown', customData, {
                    cosmosOnlyData: itemId,
                });
            }

            infoBarMessageParts = createSuggestionParts(
                itemViewState,
                clientItem,
                subject,
                isBodyTranslatable
            );
        } else if (clientItem.TranslationData.manuallyTranslated) {
            infoBarMessageParts.push(
                createTranslateMessagePart(itemViewState, clientItem, subject)
            );
        } else {
            return null;
        }

        // Display the simple info bar to translate item (no target language option yet)
        return <InfoBarMessageViewState>{
            key: key,
            source: InfoBarMessageSource.ReadingPane,
            rank: InfoBarMessageRank.Translation,
            color: InfoBarMessageColor.Green,
            messageParts: infoBarMessageParts,
        };
    }

    return null;
}

function createTranslatedParts(
    showTranslatedFrom: Boolean,
    showTranslationPrefreneces: Boolean,
    clientItem: ClientItem,
    subject: string,
    itemViewState: ItemViewState
): MessagePart[] {
    const itemId = clientItem.ItemId.Id;
    const parts: MessagePart[] = [];

    // we clear this message when navigating away from suggestion part
    if (showDeclinedAutoTranslationMessage === clientItem.ItemId.Id) {
        showDeclinedAutoTranslationMessage = null;
    }

    if (showTranslatedFrom) {
        parts.push(format(loc(rpTranslatedFromMessage), getInfoBarTranslatedLanguage(clientItem)));
    }

    parts.push(createUntranslateMessagePart(itemId, itemViewState));

    if (showTranslationPrefreneces) {
        if (shouldTranslateAutomatic(clientItem.TranslationData.itemOgmaLocale)) {
            parts.push('|');
            parts.push(createTranslationPreferencesMessagePart());
        } else {
            parts.push('|');
            parts.push(createTurnOnAutomaticMessagePart(itemViewState, itemId, subject));
            parts.push(createInfoMessage(loc(rpTurnOnAutomaticInfoToolTip)));
        }
    }
    return parts;
}

function createSuggestionParts(
    itemViewState: ItemViewState,
    clientItem: ClientItem,
    subject: string,
    isBodyTranslatable: Boolean
): MessagePart[] {
    let messageParts: MessagePart[] = [];

    if (shouldSuggestAutomatic()) {
        messageParts = [
            loc(rpTurnOnAutomaticTranslationNew),
            createYesPart(itemViewState, clientItem.ItemId.Id, subject),
            '|',
            createNoPart(clientItem),
            '|',
            createNeverMessagePart(clientItem, isBodyTranslatable),
        ];
    } else {
        if (showDeclinedAutoTranslationMessage === clientItem.ItemId.Id) {
            messageParts.push(loc(rpDeclinedAutoTranslationMessage));
        }
        messageParts.push(createTranslateMessagePart(itemViewState, clientItem, subject));
        messageParts.push('|');
        messageParts.push(createNeverMessagePart(clientItem, isBodyTranslatable));

        if (clientItem.TranslationData.translationFailed) {
            messageParts.push('|');
            messageParts.push(loc(rpTranslationErrorMessage));
        }

        if (isFeatureEnabled('rp-inlineTranslationReportWrongLanguageDebug')) {
            messageParts.push('|');
            messageParts.push(createWrongLanuagePart(clientItem));
        }
    }
    return messageParts;
}

function createYesPart(
    itemViewState: ItemViewState,
    itemId: string,
    subject: string
): InfoBarCustomAction {
    const messagePart = {
        text: loc(rpYes),
        action: async () => {
            const updateTranslationMode = await lazyUpdateTranslationMode.import();
            updateTranslationMode(TranslationMode.Automatic);
            lazyTranslateItem.importAndExecute(
                itemId,
                subject,
                itemViewState.isConversationItemPart
            );
        },
    };

    return messagePart;
}

function createNoPart(clientItem: ClientItem): InfoBarCustomAction {
    const messagePart = {
        text: loc(rpNo),
        action: async () => {
            showDeclinedAutoTranslationMessage = clientItem.ItemId.Id;
            const updateTranslationMode = await lazyUpdateTranslationMode.import();
            await updateTranslationMode(TranslationMode.Suggest);
        },
    };

    return messagePart;
}

function createInfoMessage(message: string): InfoBarInfoIcon {
    const messagePart = {
        toolTipMessage: message,
    };
    return messagePart;
}

function createTranslateMessagePart(
    itemViewState: ItemViewState,
    clientItem: ClientItem,
    subject: string
): InfoBarCustomAction {
    const messagePart = {
        text: format(loc(rpTranslateMessage), clientItem.TranslationData.userLanguage),
        action: () => {
            lazyTranslateItem.importAndExecute(
                clientItem.ItemId.Id,
                subject,
                itemViewState.isConversationItemPart
            );
        },
    };

    return messagePart;
}

function createWrongLanuagePart(clientItem: ClientItem) {
    const messagePart = {
        text: format(
            loc(rpTranslateWrongLanguageLinkMessage),
            clientItem.TranslationData.itemLocaleName
        ),
        action: () => {
            lazySetWrongLanguage.importAndExecute(clientItem.ItemId.Id);
        },
    };

    return messagePart;
}

function createNeverMessagePart(
    clientItem: ClientItem,
    isBodyTranslatable: Boolean
): InfoBarCustomAction {
    let localeName;
    let ogmaLocale;

    if (isBodyTranslatable) {
        localeName = clientItem.TranslationData.itemLocaleName;
        ogmaLocale = clientItem.TranslationData.itemOgmaLocale;
    } else {
        localeName = clientItem.TranslationData.forwardContentLocaleName;
        ogmaLocale = clientItem.TranslationData.forwardContentLocale;
    }

    const messagePart = {
        text: format(loc(rpNeverTranslateFromMessage), localeName),
        action: async () => {
            const addExcludedLanguage = await lazyAddExcludedLanguage.import();
            await addExcludedLanguage(ogmaLocale);
        },
    };

    return messagePart;
}

function createTurnOnAutomaticMessagePart(
    itemViewState: ItemViewState,
    itemId: string,
    subject: string
): InfoBarCustomAction {
    const messagePart = {
        text: loc(rpTurnOnAutomaticTranslation),
        action: async () => {
            const updateTranslationMode = await lazyUpdateTranslationMode.import();
            updateTranslationMode(TranslationMode.Automatic);
            lazyTranslateItem.importAndExecute(
                itemId,
                subject,
                itemViewState.isConversationItemPart
            );
        },
    };

    return messagePart;
}

function createTranslationPreferencesMessagePart(): InfoBarCustomAction {
    const messagePart = {
        text: loc(rpTranslationPreferences),
        action: async () => {
            lazyMountAndShowFullOptions.importAndExecute('mail', 'handling', 'inlineTranslation');
        },
    };

    return messagePart;
}

function createUntranslateMessagePart(
    itemId: string,
    itemViewState: ItemViewState
): InfoBarCustomAction {
    const messagePart = {
        text: loc(rpUntranslateMessage),
        action: () => {
            lazyUntranslateItem.importAndExecute(itemId, itemViewState.isConversationItemPart);
        },
    };

    return messagePart;
}

function getInfoBarTranslatedLanguage(clientItem: ClientItem): string {
    if (clientItem.TranslationData.isShowingTranslation) {
        return clientItem.TranslationData.itemLocaleName;
    }
    return clientItem.TranslationData.forwardContentLocaleName;
}
